import { useState, useMemo, useCallback } from 'react';

interface NDEFilters {
  consumableCategory: string[];
  consumableType: string[];
  lotNumber: string[];
  serialNumber: string[];
  manufacturer: string[];
  location: string[];
}

interface UseNDEFiltersProps {
  consumableList: any[];
}

export const useNDEFilters = ({ consumableList }: UseNDEFiltersProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<NDEFilters>({
    consumableCategory: [],
    consumableType: [],
    lotNumber: [],
    serialNumber: [],
    manufacturer: [],
    location: [],
  });

  // Extract unique consumable categories
  const uniqueConsumableCategories = useMemo(() => {
    const categories = new Set<string>();
    consumableList.forEach((consumable: any) => {
      const category = consumable.consumable_Category || consumable.consumable_category;
      if (category) {
        categories.add(String(category));
      }
    });
    return Array.from(categories).sort();
  }, [consumableList]);

  // Extract unique consumable types
  const uniqueConsumableTypes = useMemo(() => {
    const types = new Set<string>();
    consumableList.forEach((consumable: any) => {
      const type = consumable.consumable_type;
      if (type) {
        types.add(String(type));
      }
    });
    return Array.from(types).sort();
  }, [consumableList]);

  // Extract unique lot numbers
  const uniqueLotNumbers = useMemo(() => {
    const lotNumbers = new Set<string>();
    consumableList.forEach((consumable: any) => {
      const lotNum = consumable.lot_number;
      if (lotNum) {
        lotNumbers.add(String(lotNum));
      }
    });
    return Array.from(lotNumbers).sort();
  }, [consumableList]);

  // Extract unique serial numbers
  const uniqueSerialNumbers = useMemo(() => {
    const serialNumbers = new Set<string>();
    consumableList.forEach((consumable: any) => {
      const serialNum = consumable.serial_number;
      if (serialNum) {
        serialNumbers.add(String(serialNum));
      }
    });
    return Array.from(serialNumbers).sort();
  }, [consumableList]);

  // Extract unique manufacturers
  const uniqueManufacturers = useMemo(() => {
    const manufacturers = new Set<string>();
    consumableList.forEach((consumable: any) => {
      const manufacturer = consumable.manufacturer;
      if (manufacturer) {
        manufacturers.add(String(manufacturer));
      }
    });
    return Array.from(manufacturers).sort();
  }, [consumableList]);

  // Extract unique locations
  const uniqueLocations = useMemo(() => {
    const locations = new Set<string>();
    consumableList.forEach((consumable: any) => {
      const location = consumable.location;
      if (location) {
        locations.add(String(location));
      }
    });
    return Array.from(locations).sort();
  }, [consumableList]);

  // Filter consumables based on search query and filters
  const filteredConsumables = useMemo(() => {
    return consumableList.filter((consumable: any) => {
      // Search query filter - search across all fields
      const searchLower = searchQuery.toLowerCase();
      const category = (consumable.consumable_Category || consumable.consumable_category || '').toLowerCase();
      const consumableType = (consumable.consumable_type || '').toLowerCase();
      const type = (consumable.type || '').toLowerCase();
      const lotNumber = (consumable.lot_number || '').toLowerCase();
      const serialNumber = (consumable.serial_number || '').toLowerCase();
      const manufacturer = (consumable.manufacturer || '').toLowerCase();
      const location = (consumable.location || '').toLowerCase();
      const notes = (consumable.notes || '').toLowerCase();
      const materialId = (consumable.material_id || '').toLowerCase();
      
      const matchesSearch = !searchQuery || 
        category.includes(searchLower) ||
        consumableType.includes(searchLower) ||
        type.includes(searchLower) ||
        lotNumber.includes(searchLower) ||
        serialNumber.includes(searchLower) ||
        manufacturer.includes(searchLower) ||
        location.includes(searchLower) ||
        notes.includes(searchLower) ||
        materialId.includes(searchLower) ||
        (consumable.id?.toString() || '').includes(searchLower);

      // Consumable category filter
      const matchesCategory = filters.consumableCategory.length === 0 || 
        filters.consumableCategory.includes(String(consumable.consumable_Category || consumable.consumable_category || ''));

      // Consumable type filter
      const matchesConsumableType = filters.consumableType.length === 0 || 
        filters.consumableType.includes(String(consumable.consumable_type || ''));

      // Lot number filter
      const matchesLotNumber = filters.lotNumber.length === 0 || 
        filters.lotNumber.includes(String(consumable.lot_number || ''));

      // Serial number filter
      const matchesSerialNumber = filters.serialNumber.length === 0 || 
        filters.serialNumber.includes(String(consumable.serial_number || ''));

      // Manufacturer filter
      const matchesManufacturer = filters.manufacturer.length === 0 || 
        filters.manufacturer.includes(String(consumable.manufacturer || ''));

      // Location filter
      const matchesLocation = filters.location.length === 0 || 
        filters.location.includes(String(consumable.location || ''));

      return matchesSearch && matchesCategory && matchesConsumableType && matchesLotNumber && matchesSerialNumber && matchesManufacturer && matchesLocation;
    });
  }, [consumableList, searchQuery, filters]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return searchQuery !== '' || 
           filters.consumableCategory.length > 0 || 
           filters.consumableType.length > 0 ||
           filters.lotNumber.length > 0 ||
           filters.serialNumber.length > 0 ||
           filters.manufacturer.length > 0 ||
           filters.location.length > 0;
  }, [searchQuery, filters]);

  // Handle filter changes
  const onFilterChange = useCallback((key: keyof NDEFilters) => (value: string[]) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  // Reset all filters
  const onResetFilters = useCallback(() => {
    setSearchQuery('');
    setFilters({
      consumableCategory: [],
      consumableType: [],
      lotNumber: [],
      serialNumber: [],
      manufacturer: [],
      location: [],
    });
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    filters,
    onFilterChange,
    uniqueConsumableCategories,
    uniqueConsumableTypes,
    uniqueLotNumbers,
    uniqueSerialNumbers,
    uniqueManufacturers,
    uniqueLocations,
    filteredConsumables,
    hasActiveFilters,
    filteredCount: filteredConsumables.length,
    onResetFilters,
  };
};

